import { ISystemSettings } from "@/Settings/types/Common/model";
import { RootState } from "@/redux/store";
import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import SystemSettingsSideBar from "../SystemSettingsSideBar";
import SystemSettingsLoader from "../SystemSettingsLoader";
import CollapseArea from "@/Layouts/components/Basics/CollapseArea";
import WashoutReviewers from "./sections/WashoutReviewers";
import TechnicalContacts from "./sections/TechnicalContacts";
import CommissionSupport from "./sections/CommissionSupport";
import actions from "@/Constants/actionsList";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import CommonDivider from "../common/CommonDivider";
import handleScroll from "@/Settings/helpers/SystemSettings/handleScroll";

const menus = [
	{
		label: "Washout Reviewers",
		elementId: "washoutReviewers",
	},
	{
		label: "Technical Contacts",
		elementId: "technicalContacts",
	},
	{
		label: "Commission Support",
		elementId: "commissionSupport",
	},
];

const SupportContainer = () => {
	const [itemSelected, setItemSelected] = useState("washoutReviewers");

	const { useAuth } = useAuthorization();
	const technicalContactsAction = useAuth(actions.EDIT_TECHNICAL_CONTACTS);
	const commissionSupportTeamAction = useAuth(actions.EDIT_COMMISSION_SUPPORT_TEAM);
	const washoutReviewersAction = useAuth(actions.EDIT_WASHOUT_REVIEWERS);

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();
	const isLoadingSystemSettings = queryClient.getQueryState(["systemSettings", locationId])?.status === "pending";
	const isLoadingSystemSettingsAdditionalInfo =
		queryClient.getQueryState(["systemSettingsAdditionalInfo", locationId])?.status === "pending";
	const isLoadingAppendingConfigInfo = queryClient.getQueryState(["appendingConfigInfo", locationId])?.status === "pending";
	const systemSettings = queryClient.getQueryData(["systemSettings", locationId]) as ISystemSettings | null;

	const isLoading = isLoadingSystemSettings || isLoadingSystemSettingsAdditionalInfo || isLoadingAppendingConfigInfo;

	const technicalContacts = useMemo(() => systemSettings?.technicalContacts, [locationId, systemSettings?.technicalContacts]);
	const commissionSupportTeam = useMemo(() => systemSettings?.commissionSupportTeam, [locationId, systemSettings?.commissionSupportTeam]);

	const handleMenuClick = (param: { label: string; elementId: string }) => {
		setItemSelected(param.elementId);
		handleScroll(param.elementId);
	};

	const handleCollapse = (collapsed: boolean, selected: string) => {
		if (!collapsed) {
			setItemSelected(selected);
			handleScroll(selected);
		}
	};

	return (
		<Grid container className="my-[30px]">
			<Grid item xs={2}>
				<SystemSettingsSideBar menus={menus} onActionClick={handleMenuClick} selected={itemSelected} loading={isLoading} />
			</Grid>
			<Grid item xs={10}>
				<Grid className="!min-h-600px w-full ml-[30px]">
					{isLoading || !locationId ? (
						<SystemSettingsLoader />
					) : (
						<>
							<Box id="washoutReviewers">
								<CollapseArea
									title="Washout Reviewers"
									openCollapse={itemSelected === "washoutReviewers"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "washoutReviewers")}
									sx={{ padding: "0px 30px" }}
								>
									<WashoutReviewers
										locationId={locationId}
										mappedPositions={systemSettings?.mappedPositions}
										washoutReviewers={systemSettings?.washoutReviewers}
										disabled={!washoutReviewersAction}
									/>
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="technicalContacts">
								<CollapseArea
									title="Technical Contacts"
									openCollapse={itemSelected === "technicalContacts"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "technicalContacts")}
									sx={{ padding: "0px 30px" }}
								>
									<TechnicalContacts
										disabled={!technicalContactsAction}
										contacts={technicalContacts}
										locationId={locationId}
									/>
								</CollapseArea>
							</Box>
							<CommonDivider />
							<Box id="commissionSupport">
								<CollapseArea
									title="Commission Support"
									openCollapse={itemSelected === "commissionSupport"}
									collapsible
									onToggleCollapse={(collapsed) => handleCollapse(collapsed, "commissionSupport")}
									sx={{ padding: "0px 30px" }}
								>
									<CommissionSupport
										locationId={locationId}
										disabled={!commissionSupportTeamAction}
										contacts={commissionSupportTeam}
									/>
								</CollapseArea>
							</Box>
						</>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default SupportContainer;
